import { css } from "@emotion/react";
import styled from "@emotion/styled";
import {
  AspectRatio,
  Colors,
  FontSizes,
  Grid,
  GridColumn,
  Heading,
  LetterSpacings,
  LineHeights,
  MediaQueries,
  Opacities,
  Picture,
  Spacings,
  withOpacity,
} from "@polestar/component-warehouse-react";

interface StyledElementProps {
  isBig?: { desktop: boolean; mobile: boolean };
  isPreviousArticleBig?: { desktop: boolean; mobile: boolean };
}

export const StyledNewsArticle = styled(GridColumn)<StyledElementProps>(({
  isBig,
  isPreviousArticleBig,
}) => {
  return css([
    css`
      display: block;
      cursor: pointer;
      text-decoration: none;
      user-select: none;

      button {
        transition: color 0.2s;
        color: ${Colors.black};

        span {
          ${Colors.safetyOrange}
        }
      }

      img {
        transition: transform 1s;
      }

      &:hover,
      &:focus {
        button {
          color: ${withOpacity(Colors.black, Opacities.medium)};

          span {
            ${Colors.safetyOrange}
          }
        }

        img {
          transform: scale(1.03);
        }
      }

      margin-bottom: ${Spacings.none};

      ${MediaQueries.tablet} {
        margin-bottom: ${Spacings.xxxLarge};
        border: 0;
      }
    `,
    isBig?.mobile &&
      css`
        margin-bottom: ${Spacings.xLarge};
      `,
    isBig?.desktop &&
      css`
        ${MediaQueries.tablet} {
          margin-bottom: ${Spacings.xxxLarge};
        }
      `,
    !isBig?.mobile &&
      css`
        border-bottom: 1px solid ${withOpacity(Colors.black, Opacities.medium)};
      `,
    !isBig?.mobile &&
      isPreviousArticleBig?.mobile &&
      css`
        border-top: 1px solid ${withOpacity(Colors.black, Opacities.medium)};
      `,
    isBig?.mobile &&
      !isPreviousArticleBig?.mobile &&
      css`
        padding-top: ${Spacings.xLarge};
      `,
    isBig?.desktop &&
      !isPreviousArticleBig?.desktop &&
      css`
        ${MediaQueries.tablet} {
          padding-top: 0;
        }
      `,
  ]);
});

export const StyledArticle = styled.article<StyledElementProps>(({ isBig }) => {
  return css([
    css`
      position: relative;
    `,
    !isBig?.mobile &&
      css`
        padding: ${Spacings.small} 0;
      `,
    !isBig?.desktop &&
      css`
        ${MediaQueries.tablet} {
          padding: 0;
        }
      `,
  ]);
});

export const StyledGrid = styled(Grid)<StyledElementProps>(({ isBig }) => {
  return css([
    css`
      grid-row-gap: 0;

      ${MediaQueries.tablet},
      ${MediaQueries.desktop},
      ${MediaQueries.desktopWide} {
        grid-row-gap: 0;
      }
    `,
    !isBig?.desktop &&
      css`
        ${MediaQueries.desktop} {
          grid-gap: 0;
        }
      `,
  ]);
});

export const StyledImageGridColumn = styled(GridColumn)<StyledElementProps>(({
  isBig,
}) => {
  return css([
    isBig?.desktop &&
      css`
        ${MediaQueries.desktop} {
          grid-row: 1;
          grid-column-start: 5;
          grid-column-end: 13;
        }
      `,
  ]);
});

export const StyledPicture = styled(Picture)`
  position: initial;
  background-color: ${Colors.black};
`;

export const StyledContentGridColumn = styled(GridColumn)<StyledElementProps>(({
  isBig,
}) => {
  return css([
    isBig?.desktop &&
      css`
        ${MediaQueries.desktop} {
          grid-row: 1;
          grid-column-start: 1;
          grid-column-end: 4;
        }
      `,
  ]);
});

export const StyledHeadline = styled(Heading)<StyledElementProps>(({
  isBig,
}) => {
  return css([
    !isBig?.mobile &&
      css`
        font-size: ${FontSizes.body};
        line-height: ${LineHeights.small};
        letter-spacing: ${LetterSpacings.small};
        padding-right: ${Spacings.medium};
      `,
    !isBig?.desktop &&
      css`
        ${MediaQueries.tablet} {
          font-size: ${FontSizes.large};
          line-height: ${LineHeights.large};
          letter-spacing: ${LetterSpacings.large};
          padding-right: 0;
        }
      `,
  ]);
});

export const IngressWrapper = styled.div<StyledElementProps>(({ isBig }) => {
  return css([
    !isBig?.mobile &&
      css`
        display: none;
      `,
    !isBig?.desktop &&
      css`
        ${MediaQueries.tablet} {
          display: none;
        }
        ${MediaQueries.desktop} {
          display: inherit;
        }
      `,
  ]);
});

export const TextButtonWrapper = styled.div<StyledElementProps>(({ isBig }) => {
  return css([
    !isBig?.mobile &&
      css`
        display: none;
      `,
    !isBig?.desktop &&
      css`
        ${MediaQueries.tablet} {
          display: inherit;
        }
      `,
  ]);
});

export const StyledAspectRatio = styled(AspectRatio)`
  overflow: hidden;
`;

export const IconWrapper = styled.div<StyledElementProps>(({ isBig }) => {
  return css([
    css`
      position: absolute;
      top: 0;
      right: 0;
      display: flex;
      height: 100%;
      font-size: ${FontSizes.large};
      align-items: center;

      ${MediaQueries.tablet} {
        display: none;
      }
    `,
    isBig?.mobile &&
      css`
        display: none;
      `,
  ]);
});
